import * as Sentry from "@sentry/react"
import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Id } from "@stoneflow/backend/convex/_generated/dataModel"
import { createFileRoute } from "@tanstack/react-router"
import { useAction, useMutation, useQuery } from "convex/react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import type { AdminUser } from "#/components/admin/tenant-users-section"
import { TenantUsersSection } from "#/components/admin/tenant-users-section"
import { type LogGrantInput, UserAssignModal } from "#/components/admin/user-assign-modal"
import { RouteError } from "#/components/route-error"
import { Skeleton } from "#/components/ui/skeleton"
import { useIsPlatformOperator } from "#/hooks/useIsTenantAdmin"

export const Route = createFileRoute("/_authenticated/admin/tenants/$orgId/users")({
	errorComponent: RouteError,
	component: UsersTab,
})

type ClerkMember = {
	userId: string
	firstName: string | null
	lastName: string | null
	email: string | null
	role: string
}

type TenantDetail = {
	orgId: string
	name: string
	status: "active" | "trial" | "disabled"
	clerkName?: string
	members?: ClerkMember[]
	pendingInvitations?: Array<{ id: string; emailAddress: string; role: string; status: string }>
	memberCount?: number
}

function UsersTab() {
	const { orgId } = Route.useParams()
	const { isOperator, isLoading } = useIsPlatformOperator()

	const access = useQuery(api.platform.access.listOrgAccess, isOperator ? { orgId } : "skip")
	const departments = useQuery(
		api.stonelog.departments.list,
		isOperator ? { actingOrgId: orgId } : "skip",
	)
	const userDepts = useQuery(
		api.stonelog.departments.listUserDepartments,
		isOperator ? { actingOrgId: orgId } : "skip",
	)
	const getTenantDetail = useAction(api.platform.adminActions.getTenantDetail)
	const updateMemberRole = useAction(api.platform.adminActions.updateTenantMemberRole)
	const batchSet = useMutation(api.platform.access.batchSetUserLogRoles)
	const setUserDepartment = useMutation(api.stonelog.departments.setUserDepartment)

	const [tenantDetail, setTenantDetail] = useState<TenantDetail | null>(null)
	const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)

	useEffect(() => {
		if (!isOperator) return
		let cancelled = false
		;(async () => {
			try {
				const detail = (await getTenantDetail({ orgId })) as TenantDetail
				if (!cancelled) setTenantDetail(detail)
			} catch (err) {
				Sentry.captureException(err)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [isOperator, orgId, getTenantDetail])

	const deptNameById = useMemo(() => {
		const m = new Map<string, string>()
		for (const d of departments ?? []) m.set(d._id, d.name)
		return m
	}, [departments])

	// Keyed by bare Clerk userId (matches AdminUser.clerkUserId and exceptions.assignedUserId).
	const deptByUser = useMemo(() => {
		const m: Record<string, Id<"departments">> = {}
		for (const ud of userDepts ?? []) m[ud.userId] = ud.departmentId
		return m
	}, [userDepts])

	const users: AdminUser[] = useMemo(() => {
		if (!access || !tenantDetail) return []
		const userLogs = new Map<string, Id<"logs">[]>()
		for (const g of access.grants) {
			const arr = userLogs.get(g.userId) ?? []
			arr.push(g.logId)
			userLogs.set(g.userId, arr)
		}
		const list: AdminUser[] = []
		for (const m of tenantDetail.members ?? []) {
			const matchedKey =
				[...userLogs.keys()].find((k) => k === m.userId || k.endsWith(`|${m.userId}`)) ?? m.userId
			const logIds = userLogs.get(matchedKey) ?? []
			const deptId = deptByUser[m.userId]
			list.push({
				userId: matchedKey,
				clerkUserId: m.userId,
				email: m.email,
				firstName: m.firstName,
				lastName: m.lastName,
				status: "active",
				logIds,
				orgRole: m.role,
				departmentName: deptId ? deptNameById.get(deptId) : undefined,
			})
		}
		for (const inv of tenantDetail.pendingInvitations ?? []) {
			list.push({
				userId: `pending:${inv.id}`,
				email: inv.emailAddress,
				firstName: null,
				lastName: null,
				status: "pending",
				logIds: [],
			})
		}
		return list
	}, [access, tenantDetail, deptByUser, deptNameById])

	const grantsByUser: Record<
		string,
		Record<string, "viewer" | "editor" | "admin">
	> = useMemo(() => {
		const out: Record<string, Record<string, "viewer" | "editor" | "admin">> = {}
		if (!access) return out
		for (const g of access.grants) {
			out[g.userId] = out[g.userId] ?? {}
			out[g.userId][g.logId] = g.role
		}
		return out
	}, [access])

	if (isLoading) return <Skeleton className="h-64 w-full" />
	if (!isOperator) {
		return (
			<div className="py-12 text-center text-sm text-foreground-muted">
				This tab is for platform operators.
			</div>
		)
	}
	if (!access) return <Skeleton className="h-64 w-full" />

	const handleChangeOrgRole = async (clerkUserId: string, role: "org:admin" | "org:member") => {
		try {
			await updateMemberRole({ orgId, userId: clerkUserId, role })
			const detail = (await getTenantDetail({ orgId })) as TenantDetail
			setTenantDetail(detail)
			setSelectedUser((u) => (u && u.clerkUserId === clerkUserId ? { ...u, orgRole: role } : u))
			toast.success(role === "org:admin" ? "Promoted to tenant admin" : "Demoted to member")
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to update role")
			throw err
		}
	}

	const handleSave = async (grants: LogGrantInput[]) => {
		if (!selectedUser) return
		try {
			await batchSet({ orgId, userId: selectedUser.userId, grants })
			toast.success("Access updated")
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to save")
			throw err
		}
	}

	const handleChangeDepartment = async (
		clerkUserId: string,
		departmentId: Id<"departments"> | null,
	) => {
		try {
			await setUserDepartment({ actingOrgId: orgId, userId: clerkUserId, departmentId })
			toast.success(departmentId ? "Department updated" : "Department cleared")
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to update department")
			throw err
		}
	}

	return (
		<>
			<TenantUsersSection
				users={users}
				onSelectUser={setSelectedUser}
				onAddUser={() => {
					toast.info("Add-user flow lives in the existing Clerk invite dialog (TBD).")
				}}
			/>
			<UserAssignModal
				user={selectedUser}
				logs={access.logs.map((l) => ({
					_id: l._id,
					name: l.name,
					isEnabled: l.isEnabled,
				}))}
				currentGrants={selectedUser ? (grantsByUser[selectedUser.userId] ?? {}) : {}}
				departments={(departments ?? []).map((d) => ({ _id: d._id, name: d.name }))}
				currentDepartmentId={
					selectedUser?.clerkUserId ? (deptByUser[selectedUser.clerkUserId] ?? null) : null
				}
				onClose={() => setSelectedUser(null)}
				onSave={handleSave}
				onChangeOrgRole={handleChangeOrgRole}
				onChangeDepartment={handleChangeDepartment}
			/>
		</>
	)
}
